import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { geoApi, simApi } from '../lib/api'
import type { ContestRequest, ScenarioSave, SimRunRequest, SimTargetingCountRequest } from '../types/electionSim'

export function useSimBaseline(contest: ContestRequest | null) {
  return useQuery({
    queryKey: ['sim', 'baseline', contest],
    queryFn: () => simApi.baseline(contest!).then(r => r.data),
    enabled: !!contest,
    staleTime: 60_000,
  })
}

export function useSimRun() {
  return useMutation({
    mutationFn: (data: SimRunRequest) => simApi.run(data).then(r => r.data),
  })
}

export function useScenarios() {
  return useQuery({
    queryKey: ['sim', 'scenarios'],
    queryFn: () => simApi.listScenarios().then(r => r.data),
  })
}

export function useScenario(id: string | null) {
  return useQuery({
    queryKey: ['sim', 'scenarios', id],
    queryFn: () => simApi.getScenario(id!).then(r => r.data),
    enabled: !!id,
  })
}

export function useSaveScenario() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id?: string | null; data: ScenarioSave }) =>
      (id ? simApi.updateScenario(id, data) : simApi.createScenario(data)).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sim', 'scenarios'] })
    },
  })
}

export function useDeleteScenario() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => simApi.deleteScenario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sim', 'scenarios'] })
    },
  })
}

export function usePinScenario() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, geoUnitId }: { id: string; geoUnitId: string | null }) =>
      simApi.pinScenario(id, geoUnitId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sim', 'scenarios'] })
    },
  })
}

export function usePinnedScenario(geoUnitId: string | null) {
  return useQuery({
    queryKey: ['sim', 'scenarios', 'pinned', geoUnitId],
    queryFn: () => simApi.getPinnedScenario(geoUnitId!).then(r => r.data),
    enabled: !!geoUnitId,
  })
}

// Live reachable-voter count for the sim targeting panel. Pass null to disable (e.g. non-smartfilter
// modes or before a district is chosen). Key includes the full request so slider changes refetch.
export function useTargetingCount(req: SimTargetingCountRequest | null) {
  return useQuery({
    queryKey: ['sim', 'targetingCount', req],
    queryFn: async () => (await simApi.targetingCount(req!)).data.reachable,
    enabled: req != null && !!req.contest.districtGeoUnitId,
    staleTime: 60_000,
  })
}

export function useGeoUnitFullProfile(unitId: string | null) {
  return useQuery({
    queryKey: ['geo', 'full-profile', unitId],
    queryFn: () => geoApi.getFullProfile(unitId!).then(r => r.data),
    enabled: !!unitId,
    staleTime: 60_000,
  })
}
